// Voice-activity detection on the mic stream via Web Audio RMS energy — drives
// hands-free auto-submit in agent voice mode. Active only during the listening phase
// (no barge-in: the mic never records while the answer plays), and degrades to manual
// tap-to-send when Web Audio is unavailable. No dependencies.
import { useEffect, useRef } from "react";

const SAMPLE_INTERVAL_MS = 30;
const CALIBRATION_MS = 400; // initial noise-floor measurement window
const SPEECH_START_MS = 150; // sustained energy required to count as speech
const MIN_SPEECH_MS = 400; // cumulative speech before silence may submit
const SILENCE_SUBMIT_MS = 1800; // silence after speech that triggers auto-send
const THRESHOLD_FACTOR = 2.2; // speech threshold relative to the noise floor
const MIN_THRESHOLD = 0.012; // absolute floor on the RMS threshold (0..1 scale)
const FLOOR_ADAPT_RATE = 0.05; // how fast the noise floor tracks quieter conditions

let sharedContext = null;

// Create (and resume) the AudioContext inside a user gesture — Safari requires it.
export const primeVoiceActivity = () => {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return;
  if (!sharedContext) sharedContext = new Ctx();
  if (sharedContext.state === "suspended") sharedContext.resume().catch(() => {});
};

export const useVoiceActivity = ({ active, getStream, onSpeechEnd, onActivity }) => {
  const onSpeechEndRef = useRef(onSpeechEnd);
  const onActivityRef = useRef(onActivity);
  useEffect(() => {
    onSpeechEndRef.current = onSpeechEnd;
  }, [onSpeechEnd]);
  useEffect(() => {
    onActivityRef.current = onActivity;
  }, [onActivity]);

  useEffect(() => {
    if (!active) return undefined;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const stream = getStream?.();
    if (!Ctx || !stream) return undefined;

    if (!sharedContext) sharedContext = new Ctx();
    const context = sharedContext;
    if (context.state === "suspended") context.resume().catch(() => {});

    const source = context.createMediaStreamSource(stream);
    const analyser = context.createAnalyser();
    analyser.fftSize = 1024;
    source.connect(analyser);
    const samples = new Uint8Array(analyser.fftSize);

    let elapsedMs = 0;
    let noiseFloor = null;
    let aboveMs = 0; // consecutive ms above threshold
    let speechMs = 0; // cumulative speech ms this turn
    let silenceMs = 0; // consecutive ms below threshold (after speech began)
    let fired = false;

    const timer = setInterval(() => {
      if (fired) return;
      analyser.getByteTimeDomainData(samples);
      let sum = 0;
      for (let i = 0; i < samples.length; i++) {
        const v = (samples[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / samples.length);
      elapsedMs += SAMPLE_INTERVAL_MS;

      if (noiseFloor === null || elapsedMs <= CALIBRATION_MS) {
        noiseFloor = noiseFloor === null ? rms : Math.min(noiseFloor, rms);
      } else if (rms < noiseFloor) {
        noiseFloor += (rms - noiseFloor) * FLOOR_ADAPT_RATE;
      }

      const threshold = Math.max((noiseFloor ?? 0) * THRESHOLD_FACTOR, MIN_THRESHOLD);
      const talking = elapsedMs > CALIBRATION_MS && rms > threshold;

      if (talking) {
        aboveMs += SAMPLE_INTERVAL_MS;
        if (aboveMs >= SPEECH_START_MS) {
          speechMs += SAMPLE_INTERVAL_MS;
          silenceMs = 0;
        }
        // sub-threshold-duration blips freeze the countdown rather than reset it
      } else {
        aboveMs = 0;
        if (speechMs >= MIN_SPEECH_MS) silenceMs += SAMPLE_INTERVAL_MS;
      }

      onActivityRef.current?.({
        level: Math.min(1, rms * 8),
        sendProgress: speechMs >= MIN_SPEECH_MS ? Math.min(1, silenceMs / SILENCE_SUBMIT_MS) : 0,
      });

      if (speechMs >= MIN_SPEECH_MS && silenceMs >= SILENCE_SUBMIT_MS) {
        fired = true;
        onSpeechEndRef.current?.();
      }
    }, SAMPLE_INTERVAL_MS);

    return () => {
      clearInterval(timer);
      try {
        source.disconnect();
      } catch {
        /* already disconnected */
      }
      onActivityRef.current?.({ level: 0, sendProgress: 0 });
    };
  }, [active, getStream]);
};

export default useVoiceActivity;
